// Portfolio Administration v1.0 PR 3 / 4a / 4b — Loan Administration
// detail page.
//
// Renders the canonical 14-tab Entity Detail Page Layout for a single
// Loan. Mirrors the deal flow's DealDetailPage pattern: URL persistence
// via ?tab=<slug>, EntityTabBar primitive, and cross-cutting tab reuse of
// the standardized panels (Documents / Tasks / Messages / Notes /
// Activity). PR 4a wired the Insurance / UCC / Vendors / Due Dates tabs;
// PR 4b wires Defaults & Workout (§7.8) and Payment Tracking (§7.9). The
// Loan detail page is now feature-complete — no ComingSoon placeholders
// remain.
//
// The route is gated by the PortfolioAdminV1 feature flag (via
// useFeatureFlag). When the flag is off the existing PR 0 PlaceholderPage
// is rendered so the route stays functional during the rollout.

import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRightLeft,
  Banknote,
  Building2,
  CalendarClock,
  ClipboardCheck,
  CreditCard,
  Gavel,
  Landmark,
  ReceiptText,
  Scale,
  ScrollText,
  ShieldCheck,
  Target,
  Truck,
  Users,
} from "lucide-react";
import { Card } from "@/design-system/components/ui";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import { useCommunicationsRecentCount } from "@/pages/communications/hooks/useCommunications";
import { useNotesRecentCount } from "@/pages/notes/hooks/useNotes";
import { useActivityRecentCount } from "@/pages/activity/hooks/useActivityByOwner";
import { LoanHeader } from "@/components/portfolioAdmin/loanDetail/LoanHeader";
import { OverviewTab } from "@/components/portfolioAdmin/loanDetail/tabs/OverviewTab";
import { SetupTab } from "@/components/portfolioAdmin/loanDetail/tabs/SetupTab";
import { LoanIntakeProfileTab } from "@/components/portfolioAdmin/loanDetail/tabs/LoanIntakeProfileTab";
import { PartiesTab } from "@/components/portfolioAdmin/loanDetail/tabs/PartiesTab";
import { InsuranceTab } from "@/components/portfolioAdmin/loanDetail/tabs/InsuranceTab";
import { UccTab } from "@/components/portfolioAdmin/loanDetail/tabs/UccTab";
import { VendorsTab } from "@/components/portfolioAdmin/loanDetail/tabs/VendorsTab";
import { GoalsTab } from "@/components/portfolioAdmin/loanDetail/goals/GoalsTab";
import { DefaultsWorkoutTab } from "@/components/portfolioAdmin/loanDetail/tabs/DefaultsWorkoutTab";
import { MonthlyCycleTab } from "@/components/portfolioAdmin/loanDetail/tabs/MonthlyCycleTab";
import { PaymentTrackingTab } from "@/components/portfolioAdmin/loanDetail/tabs/PaymentTrackingTab";
import { QualityAssuranceTab } from "@/components/portfolioAdmin/loanDetail/tabs/QualityAssuranceTab";
import { FinancialReportingTab } from "@/components/portfolioAdmin/loanDetail/tabs/FinancialReportingTab";
import { LegalReviewTab } from "@/components/portfolioAdmin/loanDetail/tabs/LegalReviewTab";
import { FinancialsTab } from "@/components/portfolioAdmin/loanDetail/tabs/FinancialsTab";
import { FeeObligationTab } from "@/components/portfolioAdmin/loanDetail/tabs/FeeObligationTab";
import { TransitionsTab } from "@/components/portfolioAdmin/loanDetail/tabs/TransitionsTab";
import {
  useLoan,
  useLoanSetupChecklist,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useLoanHealth } from "@/hooks/portfolioAdmin/useLoanHealth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import type { LoanSetupChecklistRowDto } from "@/api/portfolioAdmin/types";
import type { HealthDiscipline } from "@/api/portfolioAdmin/loanHealthApi";

export type LoanDetailTabId =
  | "overview"
  | "setup"
  | "intake-profile"
  | "parties"
  | "insurance"
  | "ucc"
  | "vendors"
  | "goals"
  | "defaults"
  | "monthly-cycle"
  | "payment-tracking"
  | "financials"
  | "fee-obligation"
  | "transitions"
  | "legal-ops"
  | "financial-ops"
  | "legal-review"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly LoanDetailTabId[] = [
  "overview",
  "setup",
  "intake-profile",
  "parties",
  "insurance",
  "ucc",
  "vendors",
  "goals",
  "defaults",
  "monthly-cycle",
  "payment-tracking",
  "financials",
  "fee-obligation",
  "transitions",
  "legal-ops",
  "financial-ops",
  "legal-review",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

// Display labels only — tab keys/slugs are the stable deep-link contract
// (?tab=intake-profile / ?tab=legal-review / ?tab=financial-ops keep
// resolving), so the "Profile" / "Compliance" / "Financial Reporting" /
// "Quality Assurance" renames touch `label` alone. The loan strip is
// icon-only (labels wrapped to three rows at 19 tabs); aria-label +
// tooltip carry the full name on every icon.
const TAB_ITEMS: ReadonlyArray<EntityTabItem<LoanDetailTabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "setup", label: "Setup", icon: ClipboardCheck },
  { key: "intake-profile", label: "Profile", icon: ScrollText },
  { key: "parties", label: "Parties", icon: Users },
  { key: "insurance", label: "Insurance", icon: ShieldCheck },
  { key: "ucc", label: "UCC", icon: Building2 },
  { key: "vendors", label: "Vendors", icon: Truck },
  { key: "goals", label: "Goals", icon: Target },
  { key: "defaults", label: "Defaults & Workout", shortLabel: "Defaults", icon: AlertTriangle },
  { key: "monthly-cycle", label: "Monthly Cycle", shortLabel: "Cycle", icon: CalendarClock },
  { key: "payment-tracking", label: "Payment Tracking", shortLabel: "Payments", icon: CreditCard },
  { key: "financials", label: "Financials", shortLabel: "Financials", icon: Banknote },
  { key: "fee-obligation", label: "Fee & Obligation", shortLabel: "Fees", icon: ReceiptText },
  { key: "transitions", label: "Transitions", icon: ArrowRightLeft },
  { key: "legal-ops", label: "Quality Assurance", icon: Gavel },
  { key: "financial-ops", label: "Financial Reporting", icon: Landmark },
  { key: "legal-review", label: "Compliance", icon: Scale },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", shortLabel: "Comms", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

function isTabId(value: string | null): value is LoanDetailTabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/** Setup tab badge — count of incomplete DealKiller + RedFlag rows. */
function setupBadge(rows: LoanSetupChecklistRowDto[] | undefined): number | null {
  if (!Array.isArray(rows)) return null;
  const TERMINAL = new Set(["Complete", "Certified", "Waived", "NA"]);
  const open = rows.filter(
    (r) =>
      (r.catalogTier === "DealKiller" || r.catalogTier === "RedFlag") &&
      !TERMINAL.has(r.status),
  ).length;
  return open > 0 ? open : null;
}

type SeverityBadge = { count: number; tone: "warning" | "error" };

/** Discipline → tab-strip key for the severity-tinted badges. The health
 *  endpoint (LoanHealthService) is the single severity source so the tab
 *  badge, the Overview scorecard light, and the feed can never disagree. */
const BADGE_DISCIPLINE_BY_TAB: Partial<Record<LoanDetailTabId, HealthDiscipline>> = {
  insurance: "insurance",
  ucc: "ucc",
  "payment-tracking": "payments",
  "legal-ops": "qa",
  "financial-ops": "reporting",
};

/**
 * Public route component for /portfolio-admin/loans/:loanId.
 *
 * Flag PortfolioAdminV1 off → render the PR 0 PlaceholderPage so the
 * rollout can be paused without shipping a broken page.
 */
export default function LoanDetailPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Loan Servicing"
        description="Portfolio, payments, waterfalls, covenants, seasoning."
      />
    );
  }
  return <LoanDetailPageInner />;
}

function LoanDetailPageInner() {
  const { loanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const loanQuery = useLoan(loanId);
  const loan = loanQuery.data;

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: LoanDetailTabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: LoanDetailTabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  // The retired Due Dates tab redirects to Overview — its job moved to the
  // per-tab due strips and the Overview needs-attention feed. Old deep
  // links keep resolving (the unknown tab id already falls back to
  // Overview; this just cleans the URL).
  useEffect(() => {
    if (tabParam === "due-dates") setTab("overview");
  }, [tabParam, setTab]);

  // Tab badges (Layout Standard §5). Setup + cross-cutting tabs carry
  // neutral volume counts; the discipline tabs (Insurance / UCC / Payments
  // / QA / Reporting) carry worst-severity counts (amber/red) from the
  // server-computed loan health payload — the same source as the Overview
  // scorecard and feed — hidden at zero.
  const setupRows = useLoanSetupChecklist(loanId);
  const health = useLoanHealth(loanId);
  const disciplineBadges = useMemo(() => {
    const map = new Map<HealthDiscipline, SeverityBadge>();
    for (const d of health.data?.disciplines ?? []) {
      if (d.count === 0) continue;
      if (d.severity !== "error" && d.severity !== "warning") continue;
      map.set(d.discipline, { count: d.count, tone: d.severity });
    }
    return map;
  }, [health.data]);
  const badgeFor = (key: LoanDetailTabId): SeverityBadge | null => {
    const discipline = BADGE_DISCIPLINE_BY_TAB[key];
    return discipline ? (disciplineBadges.get(discipline) ?? null) : null;
  };
  const openTaskCountQuery = useOpenTaskCountByRelated("Loan", loanId);
  const openTaskCount = openTaskCountQuery.data ?? 0;
  const commsRecent = useCommunicationsRecentCount("loan", loanId ?? "", 30);
  const notesRecent = useNotesRecentCount("loan", loanId ?? "", 7);
  const activityRecent = useActivityRecentCount("loan", loanId ?? "", 7);

  if (loanQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading loan…</p>
      </Card>
    );
  }

  if (!loan) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Loan not found</h2>
        <p className="text-small text-gray-mid">
          The loan you requested was not found or you don&apos;t have permission
          to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/portfolio-admin/loans")}
        >
          Back to Loan Administration
        </button>
      </Card>
    );
  }

  const loanIdSafe = loan.id;
  const loanLabel = loan.loanNumber;

  // Covenant Calibration (Profile tab) — a single "!" warning badge on the
  // Profile tab until all three capture cards are confirmed. This is the
  // only nudge (no deadlines/reminders). Undefined (pre-feature loans) is
  // treated as no-badge; only an explicit `false` flags it.
  const profileUnconfirmed = loan.covenantCalibrationConfirmed === false;

  const tabBadge = (key: LoanDetailTabId): string | number | null => {
    const discipline = badgeFor(key);
    if (discipline) return discipline.count;
    switch (key) {
      case "setup":
        return setupBadge(setupRows.data);
      case "tasks":
        return openTaskCount > 0 ? openTaskCount : null;
      case "communications":
        return commsRecent.data && commsRecent.data > 0 ? commsRecent.data : null;
      case "notes":
        return notesRecent.data && notesRecent.data > 0 ? notesRecent.data : null;
      case "activity":
        return activityRecent.data && activityRecent.data > 0 ? activityRecent.data : null;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-space-3">
      <LoanHeader loan={loan} />

      <EntityTabBar
        ariaLabel="Loan sections"
        testIdPrefix="loan"
        items={TAB_ITEMS.map((it) => {
          // Profile tab: "!" warning badge until covenant calibration is
          // confirmed (overrides the default null badge for this tab only).
          if (it.key === "intake-profile" && profileUnconfirmed) {
            return { ...it, badge: "!", badgeTone: "warning" as const };
          }
          return {
            ...it,
            badge: tabBadge(it.key),
            badgeTone: badgeFor(it.key)?.tone,
          };
        })}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && <OverviewTab loan={loan} />}
        {tab === "setup" && <SetupTab loan={loan} />}
        {tab === "intake-profile" && <LoanIntakeProfileTab loan={loan} />}
        {tab === "parties" && <PartiesTab loan={loan} />}
        {tab === "insurance" && <InsuranceTab loan={loan} />}
        {tab === "ucc" && <UccTab loan={loan} />}
        {tab === "vendors" && <VendorsTab loan={loan} />}
        {tab === "goals" && <GoalsTab loan={loan} />}
        {tab === "defaults" && <DefaultsWorkoutTab loan={loan} />}
        {tab === "monthly-cycle" && <MonthlyCycleTab loan={loan} />}
        {tab === "payment-tracking" && <PaymentTrackingTab loan={loan} />}
        {tab === "financials" && <FinancialsTab loan={loan} />}
        {tab === "fee-obligation" && <FeeObligationTab loan={loan} />}
        {tab === "transitions" && <TransitionsTab loan={loan} />}
        {tab === "legal-ops" && <QualityAssuranceTab loan={loan} />}
        {tab === "financial-ops" && <FinancialReportingTab loan={loan} />}
        {tab === "legal-review" && <LegalReviewTab loan={loan} />}
        {tab === "documents" && (
          <DocumentPanel ownerType="Loan" ownerId={loanIdSafe} rootName={loanLabel} />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Loan"
            relatedEntityId={loanIdSafe}
            addButtonLabel="+ Add task linked to this Loan"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Loan"
            relatedEntityId={loanIdSafe}
            entityLabel={loanLabel}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="loan"
            resourceId={loanIdSafe}
            entityLabel={loanLabel}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="loan"
            ownerId={loanIdSafe}
            ownerLabel={loanLabel}
          />
        )}
        {tab === "notes" && (
          <NotesTabPanel ownerType="loan" ownerId={loanIdSafe} />
        )}
        {tab === "activity" && (
          <ActivityTabPanel ownerType="loan" ownerId={loanIdSafe} />
        )}
      </div>
    </div>
  );
}
